import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimateSharedLayout, useAnimation } from "framer-motion";
import { Card } from "../../../../components";
import { useTopikMotion } from "../topikMotion";
import { slotForPoint, topIdForDrop } from "./dragPlacement";

// Q1 - relate (§12.2): both cards are *placed* on a polarity-dependent scale. The axis reads
// "more/less probable" for NEGATIVE claims and "more/less necessary" for POSITIVE ones
// (per-polarity pole keys). Cards carry the Topik +/- suit of their polarity, matching the
// deck editor's symbolism. Submits the top-placed card as `generality`.
//
// Two equal ways to place (§1.1-A, "every move is draggable"): a card can be *dragged* into
// either slot, or *tapped* to fill the top slot. Because the scale is a strict either/or, placing
// one card always implies the other: drag a card onto "less" and the other lands on "more" (and
// vice versa); tapping fills the top and pushes the other to the bottom.
//
// Once placed, the two cards stay draggable so a swap is also "every move is draggable": drag the
// top card onto "less" (or the bottom card onto "more") and the pair trades slots - the same drop
// hit test that placed them (topIdForDrop). The ⟲ swap button remains as the explicit/keyboard
// path. A placed card released back on its own slot (or clear of both) springs home.
//
// Affordance honesty: whether tapped or dragged, the card visibly *travels* into its slot rather
// than teleporting under a new id. Each card carries a stable framer `layoutId` shared between the
// pair row and the slots, so it glides into place (and swap trades the two). Keyboard-operable:
// each pickable is a real button that places on the top slot via Enter/Space.
const TopikGeneralityStep = ({ t, polarity, cardA, cardB, submitting, onConfirm }) => {
  const { reduce, layout, deal } = useTopikMotion();
  const [topId, setTopId] = useState(null);
  // Drag feedback: whether a drag is in flight (both slots light up as live drop targets) and
  // which slot, if any, the dragged card currently hovers.
  const [dragging, setDragging] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  const topSlotRef = useRef(null);
  const bottomSlotRef = useRef(null);
  // Exactly two cards, so two stable animation controls at the top level (no hooks in a loop).
  // Used to spring a dropped-but-unplaced card back to its dealt origin.
  const controlsA = useAnimation();
  const controlsB = useAnimation();
  const controlsFor = (cardId) => (cardId === cardA.id ? controlsA : controlsB);
  // A second pair of controls for the *placed* cards: a placed card dragged but not swapped (missed
  // both slots, or released on its own slot) springs back to its slot. Separate from the pickable
  // controls above, which carry the deal variants - these only ever get object targets.
  const placedControlsA = useAnimation();
  const placedControlsB = useAnimation();
  const placedControlsFor = (cardId) => (cardId === cardA.id ? placedControlsA : placedControlsB);

  // Deal the pair in on mount (staggered), then leave the controls free for drag snap-back.
  useEffect(() => {
    controlsA.start("animate");
    controlsB.start("animate");
  }, [controlsA, controlsB]);

  let topCard = null;
  let bottomCard = null;
  if (topId === cardA.id) {
    topCard = cardA;
    bottomCard = cardB;
  } else if (topId === cardB.id) {
    topCard = cardB;
    bottomCard = cardA;
  }

  const place = (cardId) => {
    if (!submitting) setTopId(cardId);
  };
  const swap = () => setTopId((current) => (current === cardA.id ? cardB.id : cardA.id));

  // Which slot, if any, the pointer sits over. Hit-tests the live pointer (info.point) against
  // the slot rects, so the drop is accurate even if the elastic card lags the finger.
  const rectOf = (ref) => (ref.current ? ref.current.getBoundingClientRect() : null);
  const slotAtPoint = (point) => slotForPoint(point, rectOf(topSlotRef), rectOf(bottomSlotRef));

  const handleDragEnd = (cardId, info) => {
    setDragging(false);
    setDragOverSlot(null);
    const slot = submitting ? null : slotAtPoint(info.point);
    const nextTop = topIdForDrop(cardId, slot, cardA.id, cardB.id);
    if (nextTop) {
      // Dropped on a slot. "more" places this card; "less" auto-fills "more" with the *other*
      // card (the scale is a strict either/or - see topIdForDrop).
      setTopId(nextTop);
    } else {
      // Missed both slots: spring back to where it was dealt (a real placement would have
      // unmounted this element instead).
      controlsFor(cardId).start({ x: 0, y: 0, transition: layout });
    }
  };

  // A placed card was dragged. If it lands on a slot that would change the order, swap (the shared
  // layoutId remount glides both cards across, exactly like the ⟲ button). Otherwise - missed both
  // slots, or dropped back where it already sat - spring it home; this node persists (no remount),
  // so the leftover drag transform has to be animated back to 0 by hand.
  const handlePlacedDragEnd = (cardId, info) => {
    setDragging(false);
    setDragOverSlot(null);
    const slot = submitting ? null : slotAtPoint(info.point);
    const nextTop = topIdForDrop(cardId, slot, cardA.id, cardB.id);
    if (nextTop && nextTop !== topId) {
      setTopId(nextTop);
    } else {
      placedControlsFor(cardId).start({ x: 0, y: 0, transition: layout });
    }
  };

  const slotClass = (slot) => {
    const active = dragging ? " topik-generality__slot--active" : "";
    const over = dragOverSlot === slot ? " topik-generality__slot--over" : "";
    return `topik-generality__slot topik-generality__slot--${slot}${active}${over}`;
  };

  // A placed card wrapped so it travels (shared layoutId) into its slot. The `key` is tied to the
  // card id, not the slot, so a swap *remounts* each card in its new slot rather than mutating a
  // persisting node's layoutId in place. Under AnimateSharedLayout the shared layoutId then glides
  // the card across (top<->bottom). Without the key, React would keep each slot's DOM node and only
  // flip its layoutId prop, which corrupts framer v3's shared-layout tracking and leaves both cards
  // hidden (visibility:hidden) - the "both cards disappear on swap" bug.
  const placedCard = (card, prefix) => (
    <motion.div
      key={card.id}
      className="topik-generality__placed"
      layoutId={`gen-card-${card.id}`}
      transition={layout}
      animate={placedControlsFor(card.id)}
      drag={!submitting}
      dragMomentum={false}
      whileDrag={{ scale: 1.04, zIndex: 30, cursor: "grabbing" }}
      onDragStart={() => setDragging(true)}
      onDrag={(e, info) => {
        const slot = slotAtPoint(info.point);
        setDragOverSlot((prev) => (prev === slot ? prev : slot));
      }}
      onDragEnd={(e, info) => handlePlacedDragEnd(card.id, info)}
    >
      <Card
        cardId={`${prefix}-${card.id}`}
        title={card.title}
        description={card.description || ""}
        suit={polarity}
      />
    </motion.div>
  );

  return (
    <AnimateSharedLayout>
      <div className="topik-generality">
        <div className="topik-generality__scale">
          {/* Each slot is named by its pole, so a screen reader hears which end it is in
              whatever order the layout shows them. */}
          <span
            id={`topik-generality-${polarity}-more`}
            className="topik-generality__pole topik-generality__pole--top">
            {t(`topik-comparison-view.${polarity}.generality.pole-more`)}
          </span>
          <div
            ref={topSlotRef}
            className={slotClass("top")}
            role="group"
            aria-labelledby={`topik-generality-${polarity}-more`}>
            {topCard ? (
              placedCard(topCard, "gen-top")
            ) : (
              <span className="topik-generality__placeholder">
                {t("topik-comparison-view.place-hint")}
              </span>
            )}
          </div>
          <div
            ref={bottomSlotRef}
            className={slotClass("bottom")}
            role="group"
            aria-labelledby={`topik-generality-${polarity}-less`}>
            {bottomCard ? (
              placedCard(bottomCard, "gen-bottom")
            ) : (
              <span className="topik-generality__placeholder" />
            )}
          </div>
          <span
            id={`topik-generality-${polarity}-less`}
            className="topik-generality__pole topik-generality__pole--bottom">
            {t(`topik-comparison-view.${polarity}.generality.pole-less`)}
          </span>
        </div>

        {!topId ? (
          <div className="topik-view__card-pair">
            {[cardA, cardB].map((card, index) => (
              <motion.div
                key={card.id}
                className="topik-generality__pickable"
                layoutId={`gen-card-${card.id}`}
                role="button"
                tabIndex={0}
                aria-label={t("topik-comparison-view.place-card", { card: card.title })}
                custom={index}
                variants={deal}
                initial={reduce ? false : "initial"}
                animate={controlsFor(card.id)}
                transition={layout}
                drag={!submitting}
                dragMomentum={false}
                whileDrag={{ scale: 1.04, zIndex: 30, cursor: "grabbing" }}
                onDragStart={() => setDragging(true)}
                onDrag={(e, info) => {
                  const slot = slotAtPoint(info.point);
                  setDragOverSlot((prev) => (prev === slot ? prev : slot));
                }}
                onDragEnd={(e, info) => handleDragEnd(card.id, info)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    place(card.id);
                  }
                }}
              >
                <Card
                  cardId={card.id}
                  title={card.title}
                  description={card.description || ""}
                  suit={polarity}
                  onClick={() => place(card.id)}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="topik-generality__actions">
            <button
              type="button"
              className="topik-generality__swap"
              onClick={swap}
              disabled={submitting}
            >
              <span aria-hidden="true">⟲</span> {t("topik-comparison-view.swap")}
            </button>
            <button
              type="button"
              className="topik-view__submit-button"
              onClick={() => onConfirm(topId)}
              disabled={submitting}
            >
              {t("topik-comparison-view.pick-button")}
            </button>
          </div>
        )}
      </div>
    </AnimateSharedLayout>
  );
};

TopikGeneralityStep.propTypes = {
  t: PropTypes.func.isRequired,
  polarity: PropTypes.oneOf(["negative", "positive"]).isRequired,
  cardA: PropTypes.object.isRequired,
  cardB: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
};

TopikGeneralityStep.defaultProps = {
  submitting: false,
};

export default TopikGeneralityStep;
